import { useCallback, useContext, useEffect, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import ShareModal from 'components/Common/Modals/ShareModal'
import OfferBNPLAd from '../OfferBNPLAd'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ShareButton from 'components/Common/ShareWithFriendsButton'
import FlashOfferSummary from './FlashOfferSummary'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import { useAppSelector } from 'hooks/reduxHooks'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { useIntl } from 'react-intl'
import useChildrenStayFree from 'hooks/Offers/useChildrenStayFree'
import { isLEStaffOrAdmin, selectLoggedIn } from 'selectors/accountSelectors'
import {
  getCheapestSelectedPackageOptionWithDefault,
  getEffectiveCharterBundleRoute,
  getEffectiveBundledWithFlightsOnly,
  getTierProgressionPackageViews,
  isAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import { shouldShowSalesUSP } from 'selectors/featuresSelectors'
import FlashHotelPropertyDetails from './FlashHotelPropertyDetails'
import ContactUSP from '../ContactUSP'
import FlashHotelUSPs from './FlashHotelUSPs'
import Divider from 'LuxKit/Divider'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Group from 'components/utils/Group'
import OfferPageLabelGroup from '../OfferPageLabelGroup'
import OfferDescription from '../OfferDescription'
import OfferPageIntroInclusions from '../OfferPageIntroInclusions'
import TextLink from 'LuxKit/TextLink'
import ModalContext from 'contexts/ModalContext'
import FlashHotelOfferDescription from './FlashHotelMobileVariant/FlashHotelOfferDescription'
import FlashHotelInclusionsModal from './FlashHotelMobileVariant/Modals/FlashHotelInclusionsModal'
import FlashOfferIntroPricingBlock from './FlashHotelMobileVariant/FlashOfferIntroPricingBlock'
import config from 'constants/config'
import useIsFlightsDisabledForGeoState from 'hooks/Flights/useIsFlightsDisabledForGeoState'
import ExportMediaContentButton from 'components/Common/ExportMediaContentButton/ExportMediaContentButton'
import { buildAgentHubQuotingURL } from 'lib/agentHub/quoting'
import FlashOfferWithFlightsSummary from './FlashOfferWithFlightsSummary'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { hasSelectedRooms, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { take } from 'lib/array/arrayUtils'
import TextButton from 'LuxKit/Button/TextButton'
import type { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import { getCanAgentExportMedia } from 'agentHub/selectors/agentHubSelectors'
import useExperiment from 'hooks/Optimizely/useExperiment'
import useFeatureFlag from 'hooks/Optimizely/useFeatureFlag'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import { Experiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import CharterBundleUSPs from '../CharterBundle/CharterBundleUSPs'
import { SiteTakeoverCampaignUSP } from 'takeovers/utils/components/SiteTakeoverCampaignUSP'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import { TasteOfGoldPromoCard } from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoStyles'
import TasteOfGoldPromoContent from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoContent'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import { CHARTER_FLIGHT_DETAILS, NON_CHARTER_TO_CHARTER_OFFER_MAP } from 'constants/charterFlight'
import KidsFlyFreeLabel from 'components/Common/Labels/KidsFlyFreeLabel'
import useKidsFlyFree from 'hooks/CharterFlights/useKidsFlyFree'
import type { FlightsClassTypes } from 'constants/flight'
import CharterBundleBanner from '../CharterBundle/CharterBundleBanner'
import { useActiveInclusionFilters } from 'hooks/useActiveInclusionFilters'
import { reorderTileInclusionsByActiveFilters } from 'lib/offer/inclusionUtils'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'
import ContentTagLabels from 'components/Content/ContentTagLabels'
import useSuggestedPackageOption from 'hooks/OfferPage/useSuggestedPackageOption'
import SuggestedOptionPreview from '../SuggestedOptionPreview'
import FlashTierProgressionPanel from 'loyalty/components/Banners/FlashTierProgressionPanel'
import OfferPageTrustPilot from '../OfferPageTrustPilot'
import type { OfferDurationOption } from '../Filters/DurationTabs/OfferDurationTabs'
import {
  getFirstAvailableDuration,
  getFlashCalendarSkipDurationOptions,
  shouldSkipFlashDurationStep,
} from './flashHotelCalendarSkipUtils'
import MultiRoomSelectRoomsCtaButton from '../MultipleRoomsBooking/MultiRoomSelectRoomsCtaButton'
import FlashHotelHundredDollarSaleSummaryBanner from './FlashHotelHundredDollarSaleSummaryBanner'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import OfferPagePcmTestimonial from '../OfferPagePcmTestimonial'
import { hasPcmTestimonialForOfferId } from 'constants/pcmTestimonials'

const OfferIntroGrid = styled(LayoutContainer)`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};
  padding-top: ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(28)};
    padding-bottom: ${rem(28)};
    grid-template-columns: 1fr min-content;
    grid-gap: ${rem(16)} ${rem(48)};
  }
`

const MOBILE_HEADER_INCLUSION_PREVIEW_COUNT = 4

const SummaryColumn = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    flex-direction: column;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const SummaryBlock = styled.div`
  ${mediaQueryUp.desktop} {
    position: sticky;
    top: ${rem(12)};
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HotelSummary = styled(FlashOfferSummary)`
  min-width: ${rem(308)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(382)};
  }
`

const HotelFlightsSummary = styled(FlashOfferWithFlightsSummary)`
  min-width: ${rem(382)};
`

const SpacedOfferPagePcmTestimonial = styled(OfferPagePcmTestimonial)`
  && {
    margin-top: ${rem(24)};
  }
`

const CharterCallout = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
`

const CharterCalloutText = styled(Group)`
  flex: 1;
  min-width: ${rem(160)};
`

const IntroMultiRoomSelectRoomsCtaInViewRoot = styled.div`
  width: 100%;
`

interface Props {
  cheapestSelectedOption?: App.OfferPackageOptionView
  cheapestOption: App.OfferPackageOptionView
  checkIn?: string
  checkoutDate?: string
  offer: App.HotelOffer
  className?: string
  onViewPackageOptions: () => void
  flightDestination?: App.Airport
  shouldShowSalesPhoneUSP: boolean
  shareModalConfig: ShareConfig
  flightOrigin?: App.Airport
  availableForPurchase?: boolean
  rooms?: Array<App.RoomOccupants>
  charterBundleRoute?: App.CharterBundleRoute
  fareClass?: FlightsClassTypes
  packageViews?: Array<App.OfferPackageOptionView>
  durationOptions: Array<OfferDurationOption>
  currentDuration: number
  durationSelected?: boolean
  bundledWithFlightsOnly: boolean
  onMultiRoomIntroSelectRoomsCtaInViewChange?: (inView: boolean) => void
}

function FlashHotelIntro(props: Props) {
  const {
    checkIn,
    checkoutDate,
    offer,
    className,
    onViewPackageOptions,
    flightDestination,
    shouldShowSalesPhoneUSP,
    shareModalConfig,
    flightOrigin,
    cheapestOption,
    availableForPurchase,
    rooms,
    charterBundleRoute,
    fareClass,
    packageViews,
    durationOptions,
    currentDuration,
    durationSelected,
    bundledWithFlightsOnly,
    onMultiRoomIntroSelectRoomsCtaInViewChange,
  } = props
  const intl = useIntl()
  const { shouldShowKidsFlyFreeForOffer } = useKidsFlyFree({ charterBundleRoute, fareClass })
  const property = offer.property
  const showModal = useContext(ModalContext)
  const offerPageDispatch = useOfferPageDispatch()
  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])
  const charterRoute = charterBundleRoute ? CHARTER_FLIGHT_DETAILS[charterBundleRoute] : undefined

  const suggestedOption = useSuggestedPackageOption()

  const isMobile = useScreenSizeOnly('mobile')
  const { isAnyVariant: isSuggestedPackageHighInMobilePageEnabled } = useExperiment(Experiments.croSuggestedPackageHighInMobilePage, {
    canActivate: isMobile,
  })

  const isLuxuryEscapes = config.BRAND === 'luxuryescapes'
  const hasPcmTestimonial = hasPcmTestimonialForOfferId(offer.id)
  const { isAnyVariant: isPcmTestimonialEnabled } = useExperiment(
    Experiments.croPcmTestimonialOnOfferPage,
    { canActivate: !isMobile && hasPcmTestimonial },
  )

  const { isAnyVariant: isFlashCalendarSkipEnabled } = useExperiment(
    Experiments.mobileWebOfferPageFlashCalendarSkip,
    { canActivate: isMobile },
  )
  const { isAnyVariant: isNewMobileHeaderUIVariant } = useExperiment(
    Experiments.croMobileWebOfferHeader,
    { canActivate: isMobile },
  )
  const isNewMobileHeaderUIEnabled = isMobile && isNewMobileHeaderUIVariant
  const defaultOfferDuration = offer.lowestPricePackage?.duration
  const firstAvailableDuration = useMemo(
    () => getFirstAvailableDuration(durationOptions),
    [durationOptions],
  )
  const shouldSkipDurationStep = shouldSkipFlashDurationStep({
    isExperimentEnabled: isFlashCalendarSkipEnabled,
    durationSelected,
    currentDuration,
    defaultDuration: defaultOfferDuration,
    firstAvailableDuration,
  })

  const charterContact: App.GeoContact | undefined = useMemo(() => {
    if (!charterRoute) return undefined
    return {
      humanReadable: charterRoute.supportNumber.humanReadable,
      number: charterRoute.supportNumber.humanReadable.replace(/\s+/g, ''),
    }
  }, [charterRoute])

  const hasFlights = !!cheapestOption.flightPrice && !offer.flightCacheDisabled
  const isFlightsDisabledForGeoState = useIsFlightsDisabledForGeoState()
  const hideNoCacheMessaging = !hasFlights && bundledWithFlightsOnly
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled
  const showHotelPrice = !bundledWithFlightsOnly || (bundledWithFlightsOnly && flightsEnabled && !hideNoCacheMessaging && hasFlights)
  const showHotelPriceWithFlights = !bundledWithFlightsOnly && !!flightsEnabled && !hideNoCacheMessaging && hasFlights && !charterBundleRoute && !isFlightsDisabledForGeoState
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isExportMediaEnabled = config.EXPORT_MEDIA_CONTENT_ENABLED
  const isAgentHubEnabled = config.agentHub.isEnabled
  const mediaExportURL = isAgentHubEnabled ? buildAgentHubQuotingURL(offer.id, { currency, region }) : undefined
  const canAgentExportMedia = getCanAgentExportMedia
  const isNewMobileMultipleRoom = (rooms?.length ?? 0) > 1
  const isCharterLaunchOffer100FeatureEnabled = useFeatureFlag(OptimizelyFeatureFlags.charterLaunchOffer100)
  const isStaffOrAdmin = useAppSelector(isLEStaffOrAdmin)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isCharterLaunchOffer100Enabled =
    isCharterLaunchOffer100FeatureEnabled && !isAgentHubEnabled && !isStaffOrAdmin

  const [introMultiRoomSelectRoomsCtaRef, introMultiRoomSelectRoomsCtaInView] = useInView({
    threshold: 0,
    initialInView: true,
  })

  useEffect(() => {
    if (!isNewMobileMultipleRoom) {
      onMultiRoomIntroSelectRoomsCtaInViewChange?.(false)
      return
    }
    onMultiRoomIntroSelectRoomsCtaInViewChange?.(introMultiRoomSelectRoomsCtaInView)
  }, [
    introMultiRoomSelectRoomsCtaInView,
    isNewMobileMultipleRoom,
    onMultiRoomIntroSelectRoomsCtaInViewChange,
  ])

  useEffect(() => {
    return () => onMultiRoomIntroSelectRoomsCtaInViewChange?.(false)
  }, [onMultiRoomIntroSelectRoomsCtaInViewChange])

  const showHotelFlightsV2Summary = availableForPurchase && flightsEnabled && !hideNoCacheMessaging && !bundledWithFlightsOnly && !charterBundleRoute && !isFlightsDisabledForGeoState

  const activeInclusionCategories = useActiveInclusionFilters()

  // Boost tile inclusions based on active filters
  const boostedTileInclusions = useMemo(() => {
    if (!offer.tileInclusions?.length) return offer.tileInclusions
    return reorderTileInclusionsByActiveFilters(offer.tileInclusions, activeInclusionCategories)
  }, [offer.tileInclusions, activeInclusionCategories])

  const loyaltyInclusions = useLoyaltyBenefitInclusions(offer)
  const topMobileHeaderTileInclusions = useMemo(() => take(boostedTileInclusions ?? [], MOBILE_HEADER_INCLUSION_PREVIEW_COUNT), [boostedTileInclusions])
  const topMobileHeaderLoyaltyInclusions = useMemo(() => {
    const remainingInclusionCount = Math.max(0, MOBILE_HEADER_INCLUSION_PREVIEW_COUNT - topMobileHeaderTileInclusions.length)

    return take(loyaltyInclusions, remainingInclusionCount)
  }, [loyaltyInclusions, topMobileHeaderTileInclusions.length])
  const totalInclusionCount = (boostedTileInclusions?.length ?? 0) + loyaltyInclusions.length
  const isMaldivesOffer = offer.property.geoData.countryCode?.toLowerCase() === 'mv'
  const shouldShowMaldivesHighlights = isMaldivesOffer || !!charterBundleRoute

  const inclusionsLinkText = shouldShowMaldivesHighlights
    ? intl.formatMessage({
        id: 'exeNUMdSrEFO5MR8CO3q',
        defaultMessage: 'View all highlights & inclusions',
        description: 'Hyperlink text to view all highlights and inclusions',
      })
    : intl.formatMessage({
        id: 'hOvxFjXrb6hMLpGszS9i',
        defaultMessage: 'View all inclusions',
        description: 'Hyperlink text to view all inclusions on the offer detail page',
      })

  const openInclusionsModal = useCallback(async() => {
    showModal(
      <FlashHotelInclusionsModal
        additionalDescriptionMarkdown={offer.additionalDescription}
        highlightsMarkdown={shouldShowMaldivesHighlights ? offer.highlights : undefined}
        tileInclusions={boostedTileInclusions}
      />,
    )
  }, [offer, showModal, boostedTileInclusions, shouldShowMaldivesHighlights])
  const siteTakeover = useCurrentSiteTakeover({ vertical: 'hotel' })
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)

  const handleScheduleCallback = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    showModal(<RequestAgentCallbackModal type="SALES" />)
  }, [showModal])
  const handleViewSaleDates = useCallback(async() => {
    if (!isLoggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal initialMode="login" />)
      if (!result?.loggedIn) {
        return
      }
    }

    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [isLoggedIn, offerPageDispatch, showModal])

  const { capacitiesByRate, includedGuestsByRate } = useMemo(() => ({
    capacitiesByRate: offer.packages.map(pkg => pkg.roomRate?.capacities ?? []),
    includedGuestsByRate: offer.packages.map(pkg => pkg.roomRate?.includedGuests ?? []),
  }), [offer.packages])

  const { freeStayLabel } = useChildrenStayFree({
    offerType: offer.type,
    filters: { rooms },
    capacitiesByRate,
    includedGuestsByRate,
  })

  const shouldShowToolTip = useScreenSizeUp('tablet')
  return (
    <OfferIntroGrid className={className} data-testid="offer-intro">
      <Group direction="vertical" gap={20}>
        <CSSBreakpoint min="tablet">
          <LabelGroup>
            {shouldShowKidsFlyFreeForOffer(offer, checkIn) && <KidsFlyFreeLabel />}
            <ContentTagLabels offerId={offer.id} page="offer" />
            <OfferPageLabelGroup
              offer={offer}
              suppressTakeoverCampaignTagWhenUSPVisible
              freeStayLabel={freeStayLabel}
            />
          </LabelGroup>
        </CSSBreakpoint>
        <FlashHotelPropertyDetails
          property={property}
          offer={offer}
          suppressTakeoverCampaignTagWhenUSPVisible
          charterBundleRoute={charterBundleRoute}
          freeStayLabel={freeStayLabel}
          fareClass={fareClass}
          packageViews={packageViews}
          checkoutDate={checkoutDate}
          checkIn={checkIn}
          currentDuration={currentDuration}
          isOccupancyContextKnown={hasSelectedRooms(rooms)}
          rooms={rooms}
        />
        {!!offer.campaignTags?.length && offer.campaignTags.map(campaignTagId => <SiteTakeoverCampaignUSP key={campaignTagId} campaignTagId={campaignTagId} />)}
        {isTasteOfGoldActive && isTasteOfGoldEligibleOffer(offer, currency, offer.property.geoData.countryCode?.toLowerCase() !== 'au', cheapestOption.package.price) && <TasteOfGoldPromoCard>
          <CSSBreakpoint max="mobile">
            <TasteOfGoldPromoContent layout="vertical" size="medium" context="offerPage" />
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet">
            <TasteOfGoldPromoContent layout="horizontal" size="medium" context="offerPage" />
          </CSSBreakpoint>
        </TasteOfGoldPromoCard>}
        <CSSBreakpoint max="tablet">
          <Group direction="vertical" gap={20}>
            {totalInclusionCount > 2 && isNewMobileHeaderUIEnabled && <>
              <Group direction="vertical" gap={12}>
                <Heading variant="heading5">
                  {intl.formatMessage({
                    id: 'wp08YVKJcYsLYA-l15GJ',
                    defaultMessage: 'Handpicked inclusions',
                    description: 'Heading for handpicked inclusions section in the inclusions list',
                  })}
                </Heading>
                <Group direction="vertical" gap={4}>
                  <OfferPageIntroInclusions tileInclusionsList={topMobileHeaderTileInclusions} />
                  <LoyaltyInclusions inclusions={topMobileHeaderLoyaltyInclusions} variant="offer-page" size="large" />
                </Group>
              </Group>
              <Group direction="horizontal" horizontalAlign="end">
                <TextLink endIcon={<LineAngleRightIcon />} onClick={openInclusionsModal}>
                  {inclusionsLinkText}
                </TextLink>
              </Group>
            </>}
            {showHotelPrice && <FlashOfferIntroPricingBlock showHotelPriceWithFlights={showHotelPriceWithFlights} />}
            {charterRoute && (
              <>
                <Divider kind="primary" />
                <CharterCallout>
                  <CharterCalloutText direction="vertical" gap={4}>
                    <BodyText variant="medium" weight="bold">
                      {intl.formatMessage({
                        id: 'XShhVk7fv-eGHi2MdaFY',
                        defaultMessage: 'Have questions?',
                        description: 'Section heading for charter callout section on the offer detail page',
                      })}
                    </BodyText>
                    <TextLink
                      href="#"
                      size="small"
                      underline
                      onClick={handleScheduleCallback}
                    >
                      {intl.formatMessage({
                        id: 'fHVn5GopfqgCJYgNJtJA',
                        defaultMessage: 'Schedule a call back',
                        description: 'Hyperlink text to schedule a callback on the offer detail page',
                      })}
                    </TextLink>
                  </CharterCalloutText>
                  <TextButton
                    kind="secondary"
                    size="medium"
                    startIcon={<LinePhoneIcon />}
                    href={charterRoute.supportNumber.href}
                    rel="nofollow"
                  >
                    {intl.formatMessage({
                      id: 'ozmJMMhWe9QTrLpYCZ6G',
                      defaultMessage: 'Call now',
                      description: 'Button text to immediately call the customer support phone number for assistance',
                    })}
                  </TextButton>
                </CharterCallout>
              </>
            )}
            {isNewMobileMultipleRoom && <IntroMultiRoomSelectRoomsCtaInViewRoot ref={introMultiRoomSelectRoomsCtaRef}>
              <SoftOfferPageWalledGarden>
                <MultiRoomSelectRoomsCtaButton
                  checkIn={checkIn}
                  firstAvailableDuration={firstAvailableDuration}
                  shouldSkipDurationStep={shouldSkipDurationStep}
                  fit="full-width"
                />
              </SoftOfferPageWalledGarden>
            </IntroMultiRoomSelectRoomsCtaInViewRoot>}
            {NON_CHARTER_TO_CHARTER_OFFER_MAP[offer.id] && <>
              <Divider kind="primary" />
              <CharterBundleBanner offer={offer} />
            </>}
            {totalInclusionCount > 2 && !isNewMobileHeaderUIEnabled && <>
              <Divider kind="primary" />
              <Group direction="vertical" gap={12}>
                <Heading variant="heading3">
                  {intl.formatMessage({
                    id: 'wp08YVKJcYsLYA-l15GJ',
                    defaultMessage: 'Handpicked inclusions',
                    description: 'Heading for handpicked inclusions section in the inclusions list',
                  })}
                </Heading>
                <Group direction="vertical" gap={4}>
                  <OfferPageIntroInclusions tileInclusionsList={boostedTileInclusions ?? []} />
                  <LoyaltyInclusions offer={offer} variant="offer-page" size="large" />
                </Group>
              </Group>
              <TextLink size="medium" onClick={openInclusionsModal}>
                {inclusionsLinkText}
              </TextLink>
            </>}
            {offer.description && !isNewMobileHeaderUIEnabled && <>
              <Divider kind="primary" />
              <FlashHotelOfferDescription descriptionMarkdown={offer.description} facilitiesMarkdown={offer.facilities} />
            </>}
            {charterBundleRoute && <Divider kind="primary" />}
          </Group>
        </CSSBreakpoint>
        <CSSBreakpoint min="desktop">
          <Group direction="vertical" gap={20}>
            {offer.description && <OfferDescription description={offer.description} />}
            {suggestedOption && <SuggestedOptionPreview suggestedOption={suggestedOption.option} message={suggestedOption.message} viewPackageFallback={onViewPackageOptions} />}
            <SoftOfferPageWalledGarden>
              {offer.additionalDescription && <>
                <Divider kind="primary" />
                <MarkdownRender fontSize="medium" type="compact" content={offer.additionalDescription} />
              </>}
            </SoftOfferPageWalledGarden>
          </Group>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <FlashTierProgressionPanel offer={offer} packageViews={packageViews} checkoutDate={checkoutDate} />
          {suggestedOption && isSuggestedPackageHighInMobilePageEnabled && <SuggestedOptionPreview suggestedOption={suggestedOption.option} message={suggestedOption.message} viewPackageFallback={onViewPackageOptions} />}
        </CSSBreakpoint>
        <SoftOfferPageWalledGarden>
          {siteTakeover?.offerPage?.usps}
          {!charterBundleRoute && <>
            <Divider kind="primary" />
            <Group direction="vertical" gap={12}>
              <CSSBreakpoint max="tablet">
                <Heading variant="heading3">
                  {intl.formatMessage({
                    id: '2YoCLvI_Wc0RZR_r6q11',
                    defaultMessage: 'Why you should book with us',
                    description: 'Section heading for why book with us section on the offer detail page',
                  })}
                </Heading>
              </CSSBreakpoint>
              <FlashHotelUSPs offer={offer} checkIn={checkIn} onViewPackageOptions={onViewPackageOptions} />
            </Group>
          </>}
          {charterBundleRoute && <CharterBundleUSPs />}
          {shouldShowSalesPhoneUSP && !isNewMobileHeaderUIEnabled && (<>
            {!charterBundleRoute && <Divider kind="primary" />}
            <ContactUSP contactOverride={charterContact} />
          </>)}
        </SoftOfferPageWalledGarden>
      </Group>
      <SummaryColumn>
        <SummaryBlock>
          <CSSBreakpoint min="tablet">
            <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="end">
              <TripGuard inverse>
                <ShareModal shareConfig={shareModalConfig} openOnHash="#share">
                  <ShareButton horizontalOutdent={isExportMediaEnabled ? undefined : 'end'} showToolTip={shouldShowToolTip} />
                </ShareModal>
              </TripGuard>
              {isExportMediaEnabled && canAgentExportMedia && <TripGuard inverse>
                <ExportMediaContentButton href={mediaExportURL} target="_blank" />
              </TripGuard>}
              <TripGuard>
                <ShareModal shareConfig={shareModalConfig} openOnHash="#share">
                  <ShareButton showToolTip={shouldShowToolTip} />
                </ShareModal>
              </TripGuard>
              <TripGuard>
                <BookmarkButton offer={offer} />
              </TripGuard>
              {isExportMediaEnabled && canAgentExportMedia && <TripGuard>
                <ExportMediaContentButton href={mediaExportURL} target="_blank" />
              </TripGuard>}
            </Group>
          </CSSBreakpoint>
          {charterBundleRoute && isCharterLaunchOffer100Enabled && (
            <FlashHotelHundredDollarSaleSummaryBanner onViewSaleDates={handleViewSaleDates} />
          )}
          <div>
            {!showHotelFlightsV2Summary &&
              <HotelSummary
                onViewPackageOptions={onViewPackageOptions}
                flightDestination={flightDestination}
              />}
            {showHotelFlightsV2Summary &&
              <HotelFlightsSummary
                onViewPackageOptions={onViewPackageOptions}
                flightDestination={flightDestination}
              />}
          </div>
          {isLuxuryEscapes && !isMobile && hasPcmTestimonial && isPcmTestimonialEnabled && <SpacedOfferPagePcmTestimonial offerId={offer.id} />}
          <OfferPageTrustPilot />
          <OfferBNPLAd prices={allPrices} />
        </SummaryBlock>
      </SummaryColumn>
    </OfferIntroGrid>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  shouldShowSalesPhoneUSP: shouldShowSalesUSP(appState, state.offer?.lowestPricePackage?.trackingPrice ?? 0, 1000),
  cheapestOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  checkIn: state.checkInDate,
  checkoutDate: state.checkOutDate,
  offer: state.offer,
  flightOrigin: state.airport,
  availableForPurchase: isAvailableForPurchase(state, appState),
  rooms: state.rooms,
  open: !!state.activeFilter,
  charterBundleRoute: getEffectiveCharterBundleRoute(state),
  fareClass: state.fareClass,
  bundledWithFlightsOnly: getEffectiveBundledWithFlightsOnly(state),
  packageViews: getTierProgressionPackageViews(state, appState),
  durationOptions: getFlashCalendarSkipDurationOptions(state, appState),
  currentDuration: state.duration,
  durationSelected: state.durationSelected,
}), FlashHotelIntro, preventRenderWithOpenFilter)
